import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API_BASE_URL } from '../../config/api';

interface SocialPlatform {
  configured: boolean;
  error: string | null;
  name: string | null;
  followers: number;
  followersGained: number | null;
  followersLost: number | null;
  videos: number;
  views: number;
}

interface SocialDay {
  date: string;
  views: number;
}

interface SocialData {
  days: number;
  configured: boolean;
  error: string | null;
  followers: number;
  followersGained: number;
  followersLost: number;
  videos: number;
  views: number;
  series: SocialDay[];
  platforms: {
    facebook: SocialPlatform;
    instagram: SocialPlatform;
    tiktok: SocialPlatform;
    youtube: SocialPlatform;
  };
}

const RANGES = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const PLATFORMS: Array<{ id: keyof SocialData['platforms']; label: string; color: string }> = [
  { id: 'facebook', label: 'Facebook', color: '#1877f2' },
  { id: 'instagram', label: 'Instagram', color: '#db2777' },
  { id: 'tiktok', label: 'TikTok', color: '#111827' },
  { id: 'youtube', label: 'YouTube', color: '#dc2626' },
];

const formatDay = (value: string) => {
  const date = new Date(`${value}T00:00:00Z`);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatCount = (value: number | null | undefined) => (
  value == null ? '—' : value.toLocaleString()
);

const StoreDashboardSocialView: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<SocialData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/store-dashboard/social`, {
        params: { days },
        withCredentials: true,
      });
      setData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load Social');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="store-dash">
      <div className="store-dash-toolbar">
        <div>
          <div className="store-dash-kicker">Audience</div>
          <h1>Social</h1>
        </div>
        <div className="store-dash-ranges">
          {RANGES.map((range) => (
            <button
              key={range.value}
              type="button"
              className={`store-dash-range${days === range.value ? ' is-active' : ''}`}
              onClick={() => setDays(range.value)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {data && !data.configured && (
        <p className="store-dash-hint">
          Connect Facebook, Instagram, TikTok, or YouTube on the <Link to="/apis">APIs</Link> page.
        </p>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error || data?.error ? (
        <div className="alert alert-danger">{error || data?.error}</div>
      ) : (
        <>
          <div className="store-dash-cards">
            <div className="store-dash-card">
              <span>Followers</span>
              <strong>{formatCount(data?.followers)}</strong>
            </div>
            <div className="store-dash-card">
              <span>New</span>
              <strong>{formatCount(data?.followersGained)}</strong>
            </div>
            <div className="store-dash-card">
              <span>Lost</span>
              <strong>{formatCount(data?.followersLost)}</strong>
            </div>
            <div className="store-dash-card">
              <span>Videos</span>
              <strong>{formatCount(data?.videos)}</strong>
            </div>
            <div className="store-dash-card">
              <span>Views</span>
              <strong>{formatCount(data?.views)}</strong>
            </div>
          </div>

          <div className="store-dash-fleet-grid">
            {PLATFORMS.map((platform) => {
              const row = data?.platforms?.[platform.id];
              return (
                <section key={platform.id} className="store-dash-chart">
                  <div className="store-dash-chart-head">
                    <h2>{platform.label}</h2>
                    <em>{row?.name || (row?.configured ? 'Connected' : 'Not connected')}</em>
                  </div>
                  {row?.error ? (
                    <p className="store-dash-hint">{row.error}</p>
                  ) : !row?.configured ? (
                    <p className="store-dash-hint">
                      Connect {platform.label} on the <Link to="/apis">APIs</Link> page.
                    </p>
                  ) : (
                    <div className="store-dash-legend">
                      <div className="store-dash-legend-item">
                        <i style={{ background: platform.color }} />
                        <span>Followers</span>
                        <em>{formatCount(row.followers)}</em>
                      </div>
                      <div className="store-dash-legend-item">
                        <i style={{ background: platform.color }} />
                        <span>New / lost</span>
                        <em>{formatCount(row.followersGained)} / {formatCount(row.followersLost)}</em>
                      </div>
                      <div className="store-dash-legend-item">
                        <i style={{ background: platform.color }} />
                        <span>Videos</span>
                        <em>{formatCount(row.videos)}</em>
                      </div>
                      <div className="store-dash-legend-item">
                        <i style={{ background: platform.color }} />
                        <span>Views</span>
                        <em>{formatCount(row.views)}</em>
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          <section className="store-dash-chart">
            <h2>Views over time</h2>
            {(data?.series || []).every((row) => !row.views) ? (
              <p className="store-dash-hint">No view series in this range yet. TikTok only returns lifetime views on recent videos.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data?.series || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tickFormatter={formatDay} minTickGap={24} />
                  <YAxis allowDecimals={false} />
                  <Tooltip labelFormatter={formatDay} />
                  <Line type="monotone" dataKey="views" name="Views" stroke="#111827" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default StoreDashboardSocialView;
